import { useState, useRef, useCallback, useEffect } from 'react'
import { AudioRecorder } from '@/lib/audio-recorder'

export type VoiceInputState = 'idle' | 'recording' | 'transcribing'

export interface UseVoiceInputReturn {
  state: VoiceInputState
  elapsed: number
  error: string | null
  startRecording: () => Promise<void>
  stopAndTranscribe: () => Promise<string | null>
  cancel: () => void
  /** Returns a normalized amplitude value between 0 and 1, useful for voice-reactive UI */
  getAmplitude: () => number
}

/**
 * Hook for voice-to-text input. Records audio via AudioRecorder,
 * sends to POST /api/ai/transcribe, and returns the transcribed text.
 * No voice note record is created — this is for fast chat input.
 */
export function useVoiceInput(): UseVoiceInputReturn {
  const [state, setState] = useState<VoiceInputState>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const recorderRef = useRef<AudioRecorder | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  const startRecording = useCallback(async () => {
    setError(null)
    if (!recorderRef.current) {
      recorderRef.current = new AudioRecorder()
    }

    try {
      await recorderRef.current.start()
      setState('recording')
      setElapsed(0)
      timerRef.current = setInterval(() => {
        setElapsed(recorderRef.current?.getElapsedSeconds() ?? 0)
      }, 500)
    } catch {
      setError('Microphone access denied')
    }
  }, [])

  const stopAndTranscribe = useCallback(async (): Promise<string | null> => {
    if (timerRef.current) clearInterval(timerRef.current)

    if (!recorderRef.current?.isRecording) {
      setState('idle')
      return null
    }

    try {
      const result = await recorderRef.current.stop()
      setState('transcribing')

      const formData = new FormData()
      formData.append('audio', result.blob, 'recording.webm')

      const res = await fetch('/api/ai/transcribe', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error?.message || 'Transcription failed')
      }

      const data = await res.json()
      setState('idle')
      setElapsed(0)
      return data.text || null
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transcription failed')
      setState('idle')
      setElapsed(0)
      return null
    }
  }, [])

  const cancel = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (recorderRef.current?.isRecording) {
      recorderRef.current.stop().catch(() => {})
    }
    setState('idle')
    setElapsed(0)
    setError(null)
  }, [])

  const getAmplitude = useCallback((): number => {
    return recorderRef.current?.getAmplitude() ?? 0
  }, [])

  return { state, elapsed, error, startRecording, stopAndTranscribe, cancel, getAmplitude }
}
